import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownRight, FileText, Library, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  createPrompt,
  fetchCtoPrompts,
  fetchPromptUsage,
  flattenPrompts,
  nextPosition,
  slugFromTitle,
  type CtoPromptRow,
  type CtoPromptStatus,
  type PromptUsage,
} from '../../lib/ctoPrompts';

/**
 * The long-form prompts a CTO review runs with.
 *
 * A list, not an editor. The previous version put the tree and the body on one page, and the
 * body is the whole point — it wants the full width of the window, which it only gets on a
 * page of its own.
 */

const STATUS_STYLE: Record<CtoPromptStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  retired: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

function updatedWhen(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function CtoTrainingView() {
  const navigate = useNavigate();
  const [roots, setRoots] = useState<CtoPromptRow[]>([]);
  const [usage, setUsage] = useState<Map<string, PromptUsage>>(new Map());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const reloadTimer = useRef<number | null>(null);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const [tree, found] = await Promise.all([fetchCtoPrompts(), fetchPromptUsage()]);
      setRoots(tree);
      setUsage(found);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load prompts');
      if (!opts?.silent) setRoots([]);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The detail page autosaves as you type, so edits arrive in bursts. Reload on a short
  // debounce rather than per event, and silently — the list is expected to be current, not
  // to announce that it is keeping itself current.
  useEffect(() => {
    const scheduleReload = () => {
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
      reloadTimer.current = window.setTimeout(() => {
        void load({ silent: true });
      }, 400);
    };

    const channel = supabase
      .channel('cto-prompts-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'cto_prompts' }, scheduleReload)
      .subscribe();

    return () => {
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
      void supabase.removeChannel(channel);
    };
  }, [load]);

  const rows = useMemo(() => flattenPrompts(roots), [roots]);

  const addFramework = async () => {
    const title = 'New framework';
    setCreating(true);
    try {
      const created = await createPrompt({
        slug: `${slugFromTitle(title)}-${Date.now().toString(36).slice(-4)}`,
        title,
        role: 'framework',
        position: nextPosition(roots.filter((r) => r.role === 'framework')),
      });
      navigate(`/cto/frameworks/${created.id}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not create the prompt');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Library className="h-7 w-7 text-fuchsia-500" />
            Frameworks
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-3xl">
            The long-form briefs a review runs with. Found and staged are how you tell
            which ones earn their keep — not a score, just what they actually produced.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => void addFramework()}
            disabled={creating}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            New framework
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 py-16">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading prompts…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 p-10 text-center">
            No prompts yet. Start with one framework and write it long-form.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left font-medium px-4 py-3">Framework</th>
                <th className="text-left font-medium px-4 py-3 hidden xl:table-cell">Tool</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Runs when</th>
                <th
                  className="text-right font-medium px-4 py-3"
                  title="Findings attributed to this brief"
                >
                  Found
                </th>
                <th
                  className="text-right font-medium px-4 py-3"
                  title="Made the short list handed to the founder"
                >
                  Staged
                </th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map(({ prompt: p, depth }) => {
                const stats = usage.get(p.slug);
                return (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/cto/frameworks/${p.id}`)}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  <td className="px-4 py-3">
                    <div
                      className="flex items-start gap-2"
                      style={{ paddingLeft: `${depth * 20}px` }}
                    >
                      {depth > 0 ? (
                        <CornerDownRight className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" />
                      ) : (
                        <FileText className="h-4 w-4 mt-0.5 shrink-0 text-fuchsia-500" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white">{p.title}</div>
                        <div className="text-xs font-mono text-gray-400 truncate">{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    {p.fohTool ? (
                      <span className="font-mono text-xs text-gray-700 dark:text-gray-200">
                        {p.fohTool}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs hidden lg:table-cell">
                    {p.triggerMd ?? (p.role === 'framework' ? 'Always' : '—')}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                    {stats ? stats.findings : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                    {stats ? stats.staged : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLE[p.status]}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap hidden md:table-cell">
                    {updatedWhen(p.updatedAt)}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
